import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  LayoutDashboard,
  Users,
  Briefcase,
  Calendar,
  CreditCard,
  FileText,
  Settings as SettingsIcon,
  LogOut,
  Sparkles,
  Menu,
  Image,
  MapPin,
  Map as MapIcon
} from 'lucide-react';

const navigation = [
  { name: 'Tableau de bord', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Client·e·s', href: '/clients', icon: Users },
  { name: 'Zones', href: '/zones', icon: MapPin },
  { name: 'Carte', href: '/carte', icon: MapIcon },
  { name: 'Prestations', href: '/services', icon: Briefcase },
  { name: 'Rendez-vous', href: '/appointments', icon: Calendar },
  { name: 'Paiements', href: '/payments', icon: CreditCard },
  { name: 'Portfolio', href: '/my-portfolio', icon: Image },
];

const declarations = [
  { name: 'URSSAF', href: '/declarations/urssaf', icon: FileText },
  { name: 'Déclaration annuelle', href: '/declarations/annual', icon: FileText },
];

function NavigationContent({ location, onNavigate }) {
  return (
    <div className="flex flex-col h-full">
      <div className="p-6 border-b border-border/60">
        <div className="flex items-center gap-3">
          <Sparkles className="w-8 h-8 text-primary" strokeWidth={1.5} />
          <div>
            <h1 className="text-xl font-bold">AdminSAAS</h1>
          </div>
        </div>
      </div>

      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        <div className="space-y-1">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                target={item.target}
                onClick={onNavigate}
                data-testid={`nav-${item.href.replace('/', '')}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                <item.icon className="w-5 h-5" strokeWidth={1.5} />
                {item.name}
              </Link>
            );
          })}
        </div>

        <div className="pt-4 mt-4 border-t border-border/40">
          <p className="px-4 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Déclarations
          </p>
          {declarations.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={onNavigate}
                data-testid={`nav-${item.href.replace(/\//g, '-')}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                <item.icon className="w-5 h-5" strokeWidth={1.5} />
                {item.name}
              </Link>
            );
          })}
        </div>
      </nav>

      <div className="p-4 border-t border-border/60 space-y-2">
        <Link to="/settings" onClick={onNavigate}>
          <Button
            variant="ghost"
            className="w-full justify-start"
            data-testid="btn-settings"
          >
            <SettingsIcon className="w-5 h-5 mr-3" strokeWidth={1.5} />
            Paramètres
          </Button>
        </Link>
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavigate = () => {
    setOpen(false);
  };

  return (
    <div className="flex h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:w-64 border-r border-border/60 bg-card flex-col" data-testid="sidebar">
        <NavigationContent location={location} onNavigate={() => {}} />
        <div className="p-4 border-t border-border/60">
          <Button
            variant="ghost"
            className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={handleLogout}
            data-testid="btn-logout"
          >
            <LogOut className="w-5 h-5 mr-3" strokeWidth={1.5} />
            Déconnexion
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-card border-b border-border/60 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Sparkles className="w-6 h-6 text-primary" strokeWidth={1.5} />
          <div>
            <h1 className="text-lg font-bold">AdminSAAS</h1>
            <p className="text-xs text-muted-foreground">{user?.name}</p>
          </div>
        </div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
              <Menu className="w-6 h-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-64 p-0">
            <NavigationContent location={location} onNavigate={handleNavigate} />
            <div className="p-4 border-t border-border/60">
              <Button
                variant="ghost"
                className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={handleLogout}
                data-testid="btn-logout"
              >
                <LogOut className="w-5 h-5 mr-3" strokeWidth={1.5} />
                Déconnexion
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      <main className="flex-1 overflow-y-auto">
        <div className="p-4 sm:p-6 lg:p-12 pt-20 lg:pt-8" data-testid="main-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}